// Phase 0 / B0 deliverable 5 — bind the customers-realm Bearer token to the
// openapi-fetch client (ADR-007 + ADR-014 §5).
//
// react-oidc-context owns the OIDC user lifecycle and exposes the access
// token only through `useAuth()`. The API client (src/api/platform-graph.ts)
// attaches `Authorization: Bearer <token>` via a middleware that reads a
// module-level holder. This hook is the bridge: it keeps that holder in
// sync with the OIDC user, so every request the client makes — from any
// component, via TanStack Query — carries the current token without
// threading `useAuth()` through call sites.
//
// Mount ONCE near the app root (App.tsx). The effect re-runs whenever the
// access token changes (silent renew, re-login, logout → null), so the
// client never sends a stale token. Tokens stay in memory + sessionStorage
// (ADR-007) — never logged, never localStorage.

import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { setAccessToken, setAuthExpiredHandler } from "@/api/platform-graph";

import { getSigninState } from "./return-to";
import { useSessionTermination, useSessionTerminationPhase } from "./session-termination";

/**
 * Synchronizes the OIDC access token into the API client's Bearer injector.
 * Returns nothing — its sole job is the side effect. Call once at the app
 * root, inside the AuthProvider tree.
 *
 * TIMING: the sync happens DURING RENDER (ref-guarded), not in an effect.
 * React runs mount effects child-first — a routed screen whose useQuery
 * fires its first fetch in a mount effect beats the app root's effect and
 * sends the request WITHOUT the Bearer header (first paint at "/" 401'd
 * exactly this way: auth.missing_bearer on a signed-in session). The token
 * holder is a module-level variable (not React state), the write is
 * idempotent, and the ref bounds it — the same render-phase external-store
 * pattern AppShell uses for setActiveRole.
 */
export function useAuthFetch(): void {
  const auth = useAuth();
  const termination = useSessionTermination();
  const phase = useSessionTerminationPhase();
  const accessToken = phase === "active" ? auth.user?.access_token ?? null : null;

  const applied = useRef<string | null>(null);
  if (applied.current !== accessToken) {
    applied.current = accessToken;
    setAccessToken(accessToken);
  }

  // Read through a ref so the handler registered once below always drives the
  // CURRENT auth object; `auth` is a new identity on every render, and
  // re-registering per render would tear down mid-recovery.
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    let mounted = true;
    // A 401 on a bound token means the session died without the OIDC timer
    // ever firing (SEM-6327). Try to renew invisibly; if the refresh token is
    // gone too, send the user to sign in rather than leaving them in a shell
    // that looks authorized and can't load anything.
    setAuthExpiredHandler(async () => {
      if (termination.getSnapshot() !== "active") return;
      try {
        const user = await authRef.current.signinSilent();
        if (!mounted || termination.getSnapshot() !== "active") return;
        // react-oidc-context catches renewal errors and resolves null. A
        // resolved promise alone therefore does not mean the session recovered.
        if (user?.access_token && !user.expired) {
          // Bind before the middleware retries the rejected read. UserLoaded
          // schedules a React render; waiting for that render races the retry.
          applied.current = user.access_token;
          setAccessToken(user.access_token);
          return;
        }
      } catch {
        // Alternate providers may reject instead of returning null.
      }
      if (!mounted || termination.getSnapshot() !== "active") return;
      setAccessToken(null);
      try {
        await authRef.current.signinRedirect({ state: getSigninState() });
      } catch {
        // The authority itself is unreachable. RequireAuth surfaces auth.error;
        // never retry a write or start a redirect loop here.
      }
    });

    return (): void => {
      mounted = false;
      setAuthExpiredHandler(null);
    };
  }, [termination]);

  useEffect(
    () => (): void => {
      // On unmount, clear the token so a future client request can't reuse
      // a token from a torn-down session.
      setAccessToken(null);
    },
    [],
  );
}
